import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { TargetSaveAction } from '@/shared/api/generated-types';

import type { TargetReason } from '@/features/validate/domain/embedTypes';

export interface EmbedTarget {
  validationId: string;
  reason: TargetReason;
}

export interface EmbedState {
  target: EmbedTarget | null;
  targetAction: TargetSaveAction;
  preserveReference: boolean;
  targetAbandoned: boolean;
  hostChoseDocument: boolean;
  hostTargetId: string | null;
  targetRetargeted: boolean;
  targetChosen: boolean;
}

interface EmbedActions {
  setTarget: (target: EmbedTarget) => void;
  chooseTarget: (target: EmbedTarget, lockedId: string | null) => boolean;
  chooseNewValidation: (lockedId: string | null) => boolean;
  markHostChoseDocument: () => void;
  clearTarget: () => void;
  resetTarget: () => void;
  setTargetAction: (action: TargetSaveAction) => void;
  setPreserveReference: (value: boolean) => void;
  takeTargetAbandoned: () => boolean;
  takeTargetRetargeted: () => boolean;
}

type EmbedStore = EmbedState & EmbedActions;

const canChangeDestinationTo = (lockedId: string | null, validationId: string | null): boolean =>
  lockedId === null || lockedId === validationId;

const PRESERVE_REFERENCE_DEFAULT = true;
const TARGET_ACTION_DEFAULT = TargetSaveAction.Merge;

export const initialEmbedState: EmbedState = {
  target: null,
  targetAction: TARGET_ACTION_DEFAULT,
  preserveReference: PRESERVE_REFERENCE_DEFAULT,
  targetAbandoned: false,
  hostChoseDocument: false,
  hostTargetId: null,
  targetRetargeted: false,
  targetChosen: false,
};

const TARGET_CHANGE_RESET = {
  targetAction: TARGET_ACTION_DEFAULT,
  preserveReference: PRESERVE_REFERENCE_DEFAULT,
} as const;

export const useEmbedStore = create<EmbedStore>()(
  devtools(
    (set, get) => ({
      ...initialEmbedState,
      setTarget: (target) =>
        set(
          {
            ...TARGET_CHANGE_RESET,
            target,
            targetAbandoned: false,
            hostTargetId: target.validationId,
            targetRetargeted: false,
            targetChosen: false,
          },
          false,
          'setTarget'
        ),

      chooseTarget: (target, lockedId) => {
        if (!canChangeDestinationTo(lockedId, target.validationId)) return false;
        set(
          (state) => ({
            ...TARGET_CHANGE_RESET,
            target,
            targetAbandoned: false,
            targetRetargeted:
              state.targetRetargeted ||
              (state.hostTargetId !== null && target.validationId !== state.hostTargetId),
            targetChosen: true,
          }),
          false,
          'chooseTarget'
        );
        return true;
      },

      chooseNewValidation: (lockedId) => {
        if (!canChangeDestinationTo(lockedId, null)) return false;
        set(
          (state) => ({
            ...TARGET_CHANGE_RESET,
            target: null,
            targetAbandoned: false,
            targetRetargeted: state.targetRetargeted || state.hostTargetId !== null,
            targetChosen: false,
          }),
          false,
          'chooseNewValidation'
        );
        return true;
      },
      clearTarget: () =>
        set(
          {
            ...TARGET_CHANGE_RESET,
            target: null,
            targetAbandoned: true,
            targetChosen: false,
          },
          false,
          'clearTarget'
        ),
      resetTarget: () =>
        set(
          (state) => ({ ...initialEmbedState, hostChoseDocument: state.hostChoseDocument }),
          false,
          'resetTarget'
        ),
      takeTargetAbandoned: () => {
        if (!get().targetAbandoned) return false;
        set({ targetAbandoned: false }, false, 'takeTargetAbandoned');
        return true;
      },
      takeTargetRetargeted: () => {
        if (!get().targetRetargeted) return false;
        set({ targetRetargeted: false }, false, 'takeTargetRetargeted');
        return true;
      },
      markHostChoseDocument: () => set({ hostChoseDocument: true }, false, 'markHostChoseDocument'),
      setTargetAction: (targetAction) => set({ targetAction }, false, 'setTargetAction'),
      setPreserveReference: (value) =>
        set({ preserveReference: value }, false, 'setPreserveReference'),
    }),
    { name: 'EmbedStore', enabled: import.meta.env.DEV }
  )
);

export const useEmbedTarget = () => useEmbedStore((s) => s.target);

export const useTargetReason = () => useEmbedStore((s) => s.target?.reason ?? null);

export const useReplaceTargetValidationId = () =>
  useEmbedStore((s) => s.target?.validationId ?? null);

export const useTargetAction = () => useEmbedStore((s) => s.targetAction);

export const useSetTargetAction = () => useEmbedStore((s) => s.setTargetAction);

export const usePreserveReference = () => useEmbedStore((s) => s.preserveReference);

export const useSetPreserveReference = () => useEmbedStore((s) => s.setPreserveReference);

export const useHostChoseDocument = () => useEmbedStore((s) => s.hostChoseDocument);

export const markHostChoseDocument = () => useEmbedStore.getState().markHostChoseDocument();

export const takeTargetAbandoned = () => useEmbedStore.getState().takeTargetAbandoned();

export const takeTargetRetargeted = () => useEmbedStore.getState().takeTargetRetargeted();

export const setEmbedTarget = (target: EmbedTarget) => useEmbedStore.getState().setTarget(target);

export const setEmbedTargetAction = (action: TargetSaveAction) =>
  useEmbedStore.getState().setTargetAction(action);

export const clearEmbedTarget = () => useEmbedStore.getState().clearTarget();

export const resetEmbedTarget = () => useEmbedStore.getState().resetTarget();
